import Chip from '@mui/material/Chip';
import DataTable, { type DataTableColumn } from '@shared/components/DataTable';
import PanelSkeleton from '@shared/components/PanelSkeleton';
import RetryableError from '@shared/components/RetryableError';
import { ApiRequestError } from '@api/client/apiClient';
import { useLicenses } from '../hooks/useLicenses';
import type { License } from '../types';

const statusColorMap: Record<License['status'], 'success' | 'warning' | 'error'> = {
  valid: 'success',
  expiring: 'warning',
  expired: 'error',
};

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

const columns: DataTableColumn<License>[] = [
  { id: 'productType', label: 'Type', width: 70, render: (row) => row.productType },
  { id: 'permitRef', label: 'Permit', width: 110, render: (row) => row.permitRef },
  {
    id: 'status',
    label: 'Status',
    width: 90,
    render: (row) => <Chip label={row.status} color={statusColorMap[row.status]} size="small" />,
  },
  {
    id: 'expiresAt',
    label: 'Expires',
    width: 110,
    render: (row) => formatDate(row.expiresAt),
  },
];

export default function LicensesList() {
  const { data: licenses, isLoading, error, refetch } = useLicenses();

  if (isLoading) return <PanelSkeleton />;

  if (error || !licenses) {
    const correlationId =
      error instanceof ApiRequestError ? error.apiError.correlationId : undefined;
    return (
      <RetryableError
        message={error?.message ?? 'Failed to load licenses'}
        correlationId={correlationId}
        onRetry={() => void refetch()}
      />
    );
  }

  return (
    <div data-testid="licenses-list">
      <DataTable
        columns={columns}
        rows={licenses}
        rowKey={(row) => row.licenseId}
        emptyMessage="No licenses found"
      />
    </div>
  );
}
